import { useEffect, useState } from 'react'

// The theme: dark by default, the site's (IDYL's), or the one chosen on
// Account, the system's included. The choice is a per-browser convenience in
// localStorage; index.html applies it before the first paint, and data-theme
// always names the resolved theme.
export type ThemeChoice = 'system' | 'light' | 'dark'
const key = 'purlview-theme'

export function readChoice(): ThemeChoice {
  try {
    const v = window.localStorage.getItem(key)
    return v === 'light' || v === 'system' ? v : 'dark'
  } catch {
    return 'dark'
  }
}

function resolve(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(choice: ThemeChoice) {
  document.documentElement.dataset.theme = resolve(choice)
}

export function useTheme(): [ThemeChoice, (c: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(readChoice)
  useEffect(() => {
    applyTheme(choice)
    if (choice !== 'system') return
    const media = window.matchMedia?.('(prefers-color-scheme: dark)')
    const follow = () => applyTheme('system')
    media?.addEventListener('change', follow)
    return () => media?.removeEventListener('change', follow)
  }, [choice])
  const choose = (c: ThemeChoice) => {
    try {
      if (c === 'dark') window.localStorage.removeItem(key)
      else window.localStorage.setItem(key, c)
    } catch {
      // Not remembered, still applied.
    }
    setChoice(c)
  }
  return [choice, choose]
}
